import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { RefreshNotificationFlags } from '../../../../../../slices/notification-flags/refresh-notification-flags';
import {
    MarketplaceCommsHubMeetingsTab,
} from '../../MarketplaceCommsHubTabs';
import { NotificationFlag } from '../../../../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../../../../models/NotificationFlags/NotificationFlagsCategories';
import { TimelineParty } from '../../../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { MeetingParticipantsType } from '../../../../../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { TimelineEvent } from '../../../../../../slices/marketplace/timeline/models/timeline-event.model';
import { TimelineActionType } from '../../../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEventStatus } from '../../../../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { EngagementBuyer } from '../../../../../../slices/marketplace/timeline/models/engagement-buyer.model';
import { EngagementProcess } from '../../../../../../slices/marketplace/timeline/models/engagement-process.model';
import { EngagementMeeting } from '../../../../../../slices/marketplace/meetings/models/get-engagement-conference';
import { EngagementMeetingProposalContract } from '../../../../../../slices/marketplace/meetings/models/engagement-meeting-proposal-contract';
import { EngagementMeetingAvailabilityBlockContract } from '../../../../../../slices/marketplace/meetings/models/engagement-meeting-availability-block-contract';
import { useHasMeetingsOrClips } from '../../../../../../custom-hooks/useHasMeetingsOrClips';
import EngagementMeetings from '../../../../../ConnectMarketplaceDashboard/EngagementMeetings/EngagementMeetings';
import { EngagementMeetingsScheduling } from '../../../../../ConnectMarketplaceDashboard/EngagementMeetingsScheduling/EngagementMeetingsScheduling';

const ADVISOR_AND_ADMIN_PARTICIPANT_TYPES = [
    MeetingParticipantsType.SellerAdvisor,
    MeetingParticipantsType.SellerAdminSupport,
    MeetingParticipantsType.BuyerAdminSupport,
    MeetingParticipantsType.AdvisorAdminSupport,
    MeetingParticipantsType.BuyerAdvisor,
];

const BUYER_PARTICIPANT_TYPES = [MeetingParticipantsType.SellerBuyer];

interface MarketplaceMeetingsTabProps {
    engagementId: number;
    meetingsGroupId?: number;
    notificationFlagPrefix: string;
    isReadOnly?: boolean;
    party: TimelineParty;
    engagementBuyers?: EngagementBuyer[];
    isBrokerExist?: boolean;
    events: TimelineEvent[];
    engagementProcess?: EngagementProcess;
    isSfas?: boolean;
    schedulingMode: 'opportunity' | 'engagement' | 'buyerTopLevel' | 'sellerTopLevel';
    meetingPeriodStartDate?: Date;
    meetingPeriodEndDate?: Date;
    engagementSellerBuyerMeetings: EngagementMeeting[];
    meetingProposals: EngagementMeetingProposalContract[];
    availabilityBlocks: EngagementMeetingAvailabilityBlockContract[];
    onRefreshMeetings: () => void;
}

export const MarketplaceMeetingsTab = (props: MarketplaceMeetingsTabProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch() as any;

    const isAdminOrAdvisor =
        props.party === TimelineParty.VetValue ||
        props.party === TimelineParty.Broker ||
        props.party === TimelineParty.Observer;

    const hasAdvisorAndAdminMeetings = useHasMeetingsOrClips(
        props.engagementId,
        ADVISOR_AND_ADMIN_PARTICIPANT_TYPES
    );

    const hasBuyerMeetings = useHasMeetingsOrClips(
        props.engagementId,
        BUYER_PARTICIPANT_TYPES
    );

    const isMeetingSchedulingTabAvailable = useMemo(() => {
        const provideMeetingAvailabilityEvent = props.events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerProvideMeetingAvailability
        );
        const meetingsPeriodEvent = props.events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerBuyerMeetings
        );

        const isProvideInProgress =
            provideMeetingAvailabilityEvent?.status === TimelineEventStatus.InProgress ||
            provideMeetingAvailabilityEvent?.status === TimelineEventStatus.Late;
        const isMeetingsPeriodInProgress =
            meetingsPeriodEvent?.status === TimelineEventStatus.InProgress ||
            meetingsPeriodEvent?.status === TimelineEventStatus.Late;

        return isProvideInProgress || isMeetingsPeriodInProgress;
    }, [props.events]);

    const visibleTabs = useMemo(
        () =>
            [
                {
                    id: MarketplaceCommsHubMeetingsTab.AdvisorAndAdminSupportMeetings,
                    label: 'Advisor and Admin Support Meetings',
                    isVisible: isAdminOrAdvisor || hasAdvisorAndAdminMeetings,
                },
                {
                    id: MarketplaceCommsHubMeetingsTab.BuyerMeetingScheduling,
                    label: 'Buyer Meeting Scheduling',
                    isVisible: isMeetingSchedulingTabAvailable,
                },
                {
                    id: MarketplaceCommsHubMeetingsTab.BuyerMeetings,
                    label: 'Buyer Meetings',
                    isVisible: isAdminOrAdvisor || hasBuyerMeetings,
                },
            ].filter((t) => t.isVisible),
        [
            isAdminOrAdvisor,
            hasAdvisorAndAdminMeetings,
            hasBuyerMeetings,
            isMeetingSchedulingTabAvailable,
        ]
    );

    const activeTab = useMemo(() => {
        const value = Number(searchParams.get('meetingstab')) as MarketplaceCommsHubMeetingsTab;
        if (value && visibleTabs.some((t) => t.id === value)) {
            return value;
        }
        return visibleTabs[0]?.id;
    }, [searchParams, visibleTabs]);

    useEffect(() => {
        if (activeTab != null) {
            dispatch(RefreshNotificationFlags());
        }
    }, [activeTab, dispatch]);

    const handleTabChange = (tabId: MarketplaceCommsHubMeetingsTab) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('meetingstab', tabId.toString());
            return next;
        });
    };

    const meetingsPrefix = `${NotificationFlagsCategories.MpCode}.${props.engagementId}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.MeetingsCode}`;

    const tabNotificationFlags = useMemo(() => {
        const allMeetingsPrefix = `${meetingsPrefix}.`.toLowerCase();
        const buyerMeetingsPrefix = `${meetingsPrefix}.${MeetingParticipantsType.SellerBuyer}.`.toLowerCase();
        const meetingProposalPrefix = `${meetingsPrefix}.${NotificationFlagsCategories.MeetingProposalCode}.`.toLowerCase();
        return [
            {
                id: MarketplaceCommsHubMeetingsTab.AdvisorAndAdminSupportMeetings,
                notificationFlag: meetingsPrefix,
                matchPattern: (code: string) => {
                    const lower = code.toLowerCase();
                    return lower.startsWith(allMeetingsPrefix) && !lower.startsWith(buyerMeetingsPrefix) && !lower.startsWith(meetingProposalPrefix);
                },
            },
            {
                id: MarketplaceCommsHubMeetingsTab.BuyerMeetingScheduling,
                notificationFlag: `${meetingsPrefix}.${NotificationFlagsCategories.MeetingProposalCode}`,
            },
            {
                id: MarketplaceCommsHubMeetingsTab.BuyerMeetings,
                notificationFlag: `${meetingsPrefix}.${MeetingParticipantsType.SellerBuyer}`,
                matchPattern: (code: string) =>
                    code.toLowerCase().startsWith(buyerMeetingsPrefix),
            },
        ];
    }, [meetingsPrefix]);

    const getTabNotificationFlag = (tabId: MarketplaceCommsHubMeetingsTab) => {
        const config = tabNotificationFlags.find((t) => t.id === tabId);
        return config ? (
            <>
                <NotificationFlag
                    flagClassName="notification-flag-wrapper"
                    eventCode={config.notificationFlag}
                    includePrefix={!config.matchPattern}
                    matchPattern={config.matchPattern}
                    doNotRemove={!!config.matchPattern}
                />
            </>
        ) : (
            <></>
        );
    };

    const renderContent = () => {
        switch (activeTab) {
            case MarketplaceCommsHubMeetingsTab.AdvisorAndAdminSupportMeetings:
                return (
                    <EngagementMeetings
                        engagementId={props.engagementId}
                        meetingsGroupId={props.meetingsGroupId}
                        engagementBuyers={props.engagementBuyers}
                        isBrokerExist={props.isBrokerExist}
                        events={props.events}
                        engagementProcess={props.engagementProcess}
                        notificationFlagPrefix={props.notificationFlagPrefix}
                        isReadOnly={props.isReadOnly}
                        isSfas={props.isSfas}
                        participantTypes={ADVISOR_AND_ADMIN_PARTICIPANT_TYPES}
                    />
                );
            case MarketplaceCommsHubMeetingsTab.BuyerMeetingScheduling:
                return (
                    <EngagementMeetingsScheduling
                        mode={props.schedulingMode}
                        engagementId={props.engagementId}
                        startDate={props.meetingPeriodStartDate}
                        endDate={props.meetingPeriodEndDate}
                        party={props.party}
                        buyers={props.engagementBuyers}
                        onRefreshMeetings={props.onRefreshMeetings}
                        meetings={props.engagementSellerBuyerMeetings}
                        meetingProposals={props.meetingProposals}
                        availabilityBlocks={props.availabilityBlocks}
                        isReadOnly={props.isReadOnly}
                    />
                );
            case MarketplaceCommsHubMeetingsTab.BuyerMeetings:
                return (
                    <EngagementMeetings
                        engagementId={props.engagementId}
                        meetingsGroupId={props.meetingsGroupId}
                        engagementBuyers={props.engagementBuyers}
                        isBrokerExist={props.isBrokerExist}
                        events={props.events}
                        engagementProcess={props.engagementProcess}
                        notificationFlagPrefix={props.notificationFlagPrefix}
                        isReadOnly={props.isReadOnly}
                        isSfas={props.isSfas}
                        participantTypes={BUYER_PARTICIPANT_TYPES}
                        hideMeetingButtons={true}
                    />
                );
            default:
                return null;
        }
    };

    if (visibleTabs.length === 0) {
        return null;
    }

    return (
        <div className="marketplace-meetings-tab">
            <div className="RRT__container">
                <div className="RRT__tabs body-tabs">
                    {visibleTabs.map((tab) => (
                        <div
                            key={tab.id}
                            className={
                                activeTab === tab.id
                                    ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                    : 'RRT__tab'
                            }
                            onClick={() => handleTabChange(tab.id)}>
                            {tab.label}
                            {getTabNotificationFlag(tab.id)}
                        </div>
                    ))}
                </div>
            </div>
            {renderContent()}
        </div>
    );
};
